import React from 'react';
import { MapPin, Clock, Sparkles, Mail, FileText, Lightbulb } from 'lucide-react';
import { VisibilityBadge } from '../VisibilitySelector';
import { cleanTitle, keyDataSummary, stripHtml, formatDate, CATEGORY_MAP, DOC_TYPE_BADGE, STATUS_CLASSES, STATUS_LABELS } from './constants';

export function EntryCard({ entry, duplicates, navigate, variant = 'grid' }) {
  if (variant === 'list') {
    return (
      <button
        key={entry.entry_id}
        data-testid={`entry-card-${entry.entry_id}`}
        onClick={() => navigate(`/kennisbank/${entry.entry_id}`)}
        className="w-full bg-white rounded-xl border border-slate-200/80 px-4 py-3 text-left card-hover group flex items-center gap-4"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h4 className="text-sm font-medium text-slate-900 truncate group-hover:text-[#2E75B6] transition-colors duration-200">
              {cleanTitle(entry.title)}
            </h4>
            {duplicates.has(entry.entry_id) && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-medium">Mogelijk duplicaat</span>
            )}
          </div>
          <p className="text-xs text-slate-500 truncate mt-0.5">{entry.key_data?.document_type ? (keyDataSummary(entry.key_data) || stripHtml(entry.content)) : stripHtml(entry.content)}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="flex items-center gap-1 text-[10px] text-slate-400"><MapPin className="w-3 h-3" />{entry.location_id === 'all' ? 'Alle' : entry.location_id}</span>
          {entry.category && <span className={`text-[10px] px-1.5 py-0.5 rounded-full cat-${entry.category}`}>{CATEGORY_MAP[entry.category] || entry.category}</span>}
          {entry.ai_categorized && <span data-testid="ai-badge-list" className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#2E75B6]/10 text-[#2E75B6] dark:bg-[#2E75B6]/20 dark:text-[#58A6FF]"><Sparkles className="w-2.5 h-2.5" />AI</span>}
          {entry.source === 'email' && <span data-testid="email-badge-list" className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400"><Mail className="w-2.5 h-2.5" />Email</span>}
          {entry.source === 'document_upload' && <span data-testid="document-badge-list" className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-600 dark:bg-violet-500/20 dark:text-violet-400"><FileText className="w-2.5 h-2.5" />Document</span>}
          <VisibilityBadge visibility={entry.visibility} />
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_CLASSES[entry.entry_status] || 'badge-active'}`}>{STATUS_LABELS[entry.entry_status] || entry.entry_status}</span>
          <span className="text-[10px] text-slate-400">{formatDate(entry.updated_at)}</span>
        </div>
      </button>
    );
  }

  // Grid card
  return (
    <button
      key={entry.entry_id}
      data-testid={`entry-card-${entry.entry_id}`}
      onClick={() => navigate(`/kennisbank/${entry.entry_id}`)}
      className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4 text-left card-hover group h-[180px] flex flex-col"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="text-sm font-medium text-slate-900 dark:text-[#E6EDF3] line-clamp-2 group-hover:text-[#2E75B6] transition-colors duration-200">
          {cleanTitle(entry.title)}
        </h4>
        <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
          {duplicates.has(entry.entry_id) && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-medium">Mogelijk duplicaat</span>
          )}
          <VisibilityBadge visibility={entry.visibility} />
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_CLASSES[entry.entry_status] || 'badge-active'}`}>
            {STATUS_LABELS[entry.entry_status] || entry.entry_status}
          </span>
        </div>
      </div>
      {entry.key_data?.document_type ? (
        <div className="mb-auto">
          <span className="inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-600 mb-1">
            {DOC_TYPE_BADGE[entry.key_data.document_type] || 'Document'}
          </span>
          {keyDataSummary(entry.key_data) && (
            <p className="text-xs text-slate-600 dark:text-[#8B949E] line-clamp-1 mt-0.5">{keyDataSummary(entry.key_data)}</p>
          )}
        </div>
      ) : (
        <p className="text-xs text-slate-500 dark:text-[#8B949E] line-clamp-2 mb-auto">{stripHtml(entry.content)}</p>
      )}
      <div className="flex items-center gap-2 flex-wrap mt-2 pt-2 border-t border-slate-100 dark:border-[#21262D]">
        <span className="flex items-center gap-1 text-[10px] text-slate-400"><MapPin className="w-3 h-3" />{entry.location_id === 'all' ? 'Alle' : entry.location_id}</span>
        {entry.category && <span className={`text-[10px] px-1.5 py-0.5 rounded-full cat-${entry.category}`}>{CATEGORY_MAP[entry.category] || entry.category}</span>}
        {entry.ai_categorized && <span data-testid="ai-badge" className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#2E75B6]/10 text-[#2E75B6] dark:bg-[#2E75B6]/20 dark:text-[#58A6FF]"><Sparkles className="w-2.5 h-2.5" />AI</span>}
        {entry.ai_normalize_suggestions?.length > 0 && <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600"><Lightbulb className="w-2.5 h-2.5" />{entry.ai_normalize_suggestions.length}</span>}
        {entry.source === 'email' && <span data-testid="email-badge" className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400"><Mail className="w-2.5 h-2.5" />Email</span>}
        {entry.source === 'document_upload' && <span data-testid="document-badge" className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-600 dark:bg-violet-500/20 dark:text-violet-400"><FileText className="w-2.5 h-2.5" />Document</span>}
        <span className="flex items-center gap-1 text-[10px] text-slate-400 ml-auto"><Clock className="w-3 h-3" />{formatDate(entry.updated_at)}</span>
      </div>
    </button>
  );
}
